import React, { useRef, useEffect } from "react";
import { useLongPress } from "use-long-press";
import { States } from "../App.js";

export function SpinTheWheelPage({ spinnerValues, setState }) {
  return (
    <div className="fullcontent SpinnerPage">
      <Spinner
        spinnerValues={spinnerValues
          .filter((spinnerValue) => spinnerValue.visible)
          .map((spinnerValue) => spinnerValue.text)}
        setState={setState}
      />
      <div className="spinner-result" id="spinner-result"></div>
    </div>
  );
}

function Spinner({ spinnerValues, setState }) {
  const canvasRef = useRef(null);
  const rotation = useRef(Math.random() * 2 * Math.PI);
  const velocity = useRef(0);

  const spinnerHuesRef = useRef(null);
  const cachedImageRef = useRef(null);
  useEffect(() => {
    spinnerHuesRef.current = spinnerValues.map(() => Math.random() * 360);

    cachedImageRef.current = generateCachedImage(
      spinnerValues,
      spinnerHuesRef.current,
    );
  }, [spinnerValues]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    let animationFrameId;
    let previousTimestep;
    const render = (timestep) => {
      if (!previousTimestep) {
        previousTimestep = timestep;
      }

      const dt = (timestep - previousTimestep) / 1000;
      rotation.current += velocity.current * dt;
      velocity.current -= 0.8 * dt;
      if (velocity.current < 0) {
        velocity.current = 0;
      }

      const selectedValue =
        spinnerValues.length -
        Math.floor(
          (spinnerValues.length * (rotation.current % (2 * Math.PI))) /
            (2 * Math.PI),
        ) -
        1;

      const spinnerResultElement = document.getElementById("spinner-result");
      if (spinnerResultElement) {
        spinnerResultElement.textContent = spinnerValues[selectedValue];
      }

      // prettier-ignore
      drawCanvas(
        ctx,cachedImageRef.current,rotation.current,
        spinnerValues,selectedValue,spinnerHuesRef.current
      );

      animationFrameId = window.requestAnimationFrame(render);
      previousTimestep = timestep;
    };
    window.requestAnimationFrame(render);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  });

  const onClick = () => {
    velocity.current = Math.random() * 5 + 5;
  };

  const onRightClick = (e) => {
    e.preventDefault();
    setState(States.EditSpinner);
  };

  const bindLongPress = useLongPress(() => {
    setState(States.EditSpinner);
  });

  return (
    <canvas
      className="spinner"
      ref={canvasRef}
      onClick={onClick}
      onContextMenu={onRightClick}
      {...bindLongPress()}
    />
  );
}

function generateCachedImage(spinnerValues, spinnerHues) {
  let imageCanvas = document.getElementById("cached-image-canvas");
  if (!imageCanvas) {
    imageCanvas = document.createElement("canvas");
    imageCanvas.id = "cached-image-canvas";
    imageCanvas.style.display = "none";
    imageCanvas.width = imageCanvas.height = 600;
    document.body.appendChild(imageCanvas);
  }

  const ctx = imageCanvas.getContext("2d");
  const x = imageCanvas.width / 2;
  const y = imageCanvas.height / 2;
  const r = Math.min(imageCanvas.width / 2, imageCanvas.height / 2);

  for (let i = 0; i < spinnerValues.length; i++) {
    // prettier-ignore
    drawSegment(ctx,x,y,r,0,spinnerValues.length,i,spinnerValues[i],spinnerHues[i],false);
  }

  const cachedImage = new Image();
  cachedImage.src = imageCanvas.toDataURL();

  return cachedImage;
}

function drawCanvas(
  ctx,
  cachedImage,
  rotation,
  spinnerValues,
  selectedValue,
  spinnerHues,
) {
  const canvasBounds = ctx.canvas.parentElement.getBoundingClientRect();
  const parentWidth = canvasBounds.width;
  const parentHeight = canvasBounds.height;

  ctx.canvas.width = parentWidth;
  ctx.canvas.height = parentHeight;

  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  if (!spinnerValues) {
    return;
  }

  const x = parentWidth / 2;
  const y = parentHeight / 2;
  const r = Math.min(parentWidth / 2, parentHeight / 2) * 0.99;

  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.drawImage(cachedImage, -r, -r, r * 2, r * 2);
  ctx.resetTransform();

  // prettier-ignore
  drawSegment(
    ctx,x,y,r,rotation,spinnerValues.length,selectedValue,
    spinnerValues[selectedValue],spinnerHues[selectedValue],true
  );
}

function drawSegment(
  ctx,
  x,
  y,
  r,
  rotation,
  nSegments,
  segmentIndex,
  segmentText,
  segmentHue,
  segmentHighlighted,
) {
  const fontHeight = r * 0.072;
  const leftMargin = r * 0.15;
  const rightMargin = r * 0.02;

  ctx.strokeStyle = "black";
  ctx.lineWidth = r / 140;
  ctx.font = `${fontHeight}px sans-serif`;

  if (segmentHighlighted) {
    ctx.fillStyle = `hsl(${segmentHue}, 80%, 80%)`;
  } else {
    ctx.fillStyle = `hsl(${segmentHue}, 25%, 25%)`;
  }

  const angleDelta = (2 * Math.PI) / nSegments;
  const startAngle = rotation + segmentIndex * angleDelta;
  const endAngle = rotation + (segmentIndex + 1) * angleDelta;

  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.arc(x, y, r, startAngle, endAngle);
  ctx.lineTo(x, y);
  ctx.fill();

  if (nSegments > 1) {
    ctx.stroke();
  }

  ctx.fillStyle = "black";

  ctx.translate(x, y);
  ctx.rotate(rotation + (segmentIndex + 0.5) * angleDelta);
  ctx.fillText(
    segmentText,
    leftMargin,
    fontHeight / 3,
    r - rightMargin - leftMargin,
  );
  ctx.resetTransform();
}
